// Button Component - Theme-aware Cosmic Design
import React from 'react';
import { motion } from 'framer-motion';

/**
 * Button variants:
 * - primary: Themed accent gradient, main CTAs
 * - secondary: Glass effect with theme tint, secondary actions
 * - ghost: Transparent with accent border, tertiary actions
 * - danger: Red toned for destructive actions
 * - icon: Icon-only circular button
 */

const sizes = {
    sm: 'px-3 py-1.5 text-sm rounded-lg',
    md: 'px-5 py-2.5 text-base rounded-xl',
    lg: 'px-8 py-3.5 text-lg rounded-xl',
    xl: 'px-10 py-4 text-xl rounded-2xl'
};

export const Button = React.forwardRef(({
    children,
    variant = 'primary',
    size = 'md',
    className = '',
    disabled = false,
    loading = false,
    icon: Icon,
    iconPosition = 'left',
    onClick,
    type = 'button',
    ...props
}, ref) => {
    const baseStyles = `
        relative overflow-hidden
        inline-flex items-center justify-center gap-2
        font-medium
        transition-all duration-300 ease-out
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900
        disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none
    `;

    // Variant-specific styles using CSS variables
    const getVariantStyles = () => {
        switch (variant) {
            case 'primary':
                return {
                    className: 'text-gray-900 font-semibold active:scale-[0.98]',
                    style: {
                        background: 'var(--theme-button-primary)',
                        boxShadow: '0 4px 15px var(--theme-accent-glow)',
                    }
                };
            case 'secondary':
                return {
                    className: 'backdrop-blur-md hover:brightness-110 active:brightness-95',
                    style: {
                        background: 'var(--theme-button-secondary-bg)',
                        border: '1px solid var(--theme-button-secondary-border)',
                        color: 'var(--theme-text)',
                    }
                };
            case 'ghost':
                return {
                    className: 'bg-transparent hover:bg-white/5',
                    style: {
                        border: '1px solid var(--theme-primary)',
                        color: 'var(--theme-text)',
                    }
                };
            case 'danger':
                return {
                    className: 'text-white font-semibold',
                    style: {
                        background: 'linear-gradient(135deg, var(--theme-error), #dc2626)',
                        boxShadow: '0 4px 15px rgba(239, 68, 68, 0.25)',
                    }
                };
            case 'icon':
                return {
                    className: 'backdrop-blur-md p-2.5 rounded-full',
                    style: {
                        background: 'var(--theme-button-secondary-bg)',
                        border: '1px solid var(--theme-glass-border)',
                        color: 'var(--theme-text)',
                    }
                };
            default:
                return { className: '', style: {} };
        }
    };

    const variantConfig = getVariantStyles();
    const sizeStyles = variant === 'icon' ? '' : (sizes[size] || sizes.md);

    return (
        <motion.button
            ref={ref}
            type={type}
            disabled={disabled || loading}
            onClick={onClick}
            className={`${baseStyles} ${variantConfig.className} ${sizeStyles} ${className}`}
            style={variantConfig.style}
            whileHover={{ scale: disabled ? 1 : 1.02, filter: 'brightness(1.1)' }}
            whileTap={{ scale: disabled ? 1 : 0.98 }}
            {...props}
        >
            {/* Shimmer effect on hover */}
            <span className="absolute inset-0 overflow-hidden rounded-inherit">
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700" />
            </span>

            {/* Loading spinner */}
            {loading && (
                <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                    />
                    <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                </svg>
            )}

            {/* Icon left */}
            {Icon && iconPosition === 'left' && variant !== 'icon' && !loading && (
                <Icon className="w-5 h-5" />
            )}

            {/* Button text */}
            {variant !== 'icon' && (
                <span className="relative z-10">{children}</span>
            )}

            {/* Icon only for icon variant */}
            {variant === 'icon' && Icon && !loading && (
                <Icon className="w-5 h-5" />
            )}

            {/* Icon right */}
            {Icon && iconPosition === 'right' && variant !== 'icon' && !loading && (
                <Icon className="w-5 h-5" />
            )}
        </motion.button>
    );
});

Button.displayName = 'Button';

export default Button;
